import {
  ITimeOffRequestPaginatedListData,
  ITimeOffRequestService,
  IUpdateTimeOffStatePayload
} from './timeoff-request.types';
import { AxiosInstance } from 'axios';
import { IFetchTimeOffRequestPayload } from '@/services/timeoff-request';
import { objectToFormData } from '@/utils/objectToFormData';
import {
  ITimeOffRequestData,
  TimeOffStateEnum
} from '@/models/timeoff-request';
import { ITransition } from '@/models/transition';
import { TIME_OFF_REQUESTS_EXPORT_XLSX_TIMEOUT } from './timeoff-request.constants';
import {
  IAttachmentData,
  IMentionAttachmentCommentData,
  IUserData
} from '@/models';

export class TimeOffRequestService implements ITimeOffRequestService {
  private endpoint: string;

  constructor(private axios: AxiosInstance) {
    this.endpoint = 'v1/timeoffs/requests/';
  }

  fetchTimeOffRequests(
    params?: IFetchTimeOffRequestPayload
  ): Promise<ITimeOffRequestData[] | ITimeOffRequestPaginatedListData> {
    return this.axios
      .get<ITimeOffRequestData[] | ITimeOffRequestPaginatedListData>(
        `${this.endpoint}`,
        {
          params
        }
      )
      .then(({ data }) => data);
  }

  fetchTimeOffRequest(id: number): Promise<ITimeOffRequestData> {
    return this.axios
      .get<ITimeOffRequestData>(`${this.endpoint}${id}/`)
      .then(({ data }) => data);
  }

  createTimeOffRequest(
    data: ITimeOffRequestData
  ): Promise<ITimeOffRequestData> {
    const formData = objectToFormData(data);
    return this.axios
      .post<ITimeOffRequestData>(`${this.endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  updateTimeOffRequest(
    data: ITimeOffRequestData
  ): Promise<ITimeOffRequestData> {
    const formData = objectToFormData(data);
    return this.axios
      .patch<ITimeOffRequestData>(`${this.endpoint}${data.id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(({ data }) => data);
  }

  deleteTimeOffRequest(id: number): Promise<any> {
    return this.axios.delete(`${this.endpoint}${id}/`);
  }

  exportTimeOffRequestsXLSX(params?: IFetchTimeOffRequestPayload) {
    return this.axios
      .get<Blob>(`${this.endpoint}export-xlsx/`, {
        params: params,
        responseType: 'blob',
        timeout: TIME_OFF_REQUESTS_EXPORT_XLSX_TIMEOUT
      })
      .then(({ data }) => data);
  }

  updateTimeOffRequestState(
    data: IUpdateTimeOffStatePayload
  ): Promise<ITimeOffRequestData> {
    return this.axios
      .put<ITimeOffRequestData>(
        `${this.endpoint}${data.id}/set_state/${data.state}/`,
        {
          assignee: data.assignee,
          comment: data.comment
        },
        {
          params: data.params
        }
      )
      .then(({ data }) => data);
  }

  downloadTimeOffAttachment(id: number, attachmentId: number) {
    return this.axios
      .get<Blob>(`${this.endpoint}${id}/download-attachment/${attachmentId}/`, {
        responseType: 'blob'
      })
      .then(({ data }) => data);
  }

  deleteTimeOffAttachment(id: number, attachmentId: number) {
    return this.axios
      .delete(`${this.endpoint}${id}/remove-attachment/${attachmentId}/`)
      .then(({ data }) => data);
  }

  fetchTimeOffRequestActivities(id: number): Promise<ITransition[]> {
    return this.axios
      .get<ITransition[]>(`${this.endpoint}${id}/activities/`)
      .then(({ data }) => data);
  }

  // TIME-OFF COMMENTS
  fetchTimeOffRequestComments(
    timeOffId: number
  ): Promise<IMentionAttachmentCommentData[]> {
    return this.axios
      .get<IMentionAttachmentCommentData[]>(
        `${this.endpoint}${timeOffId}/comments/`
      )
      .then(({ data }) => data);
  }

  fetchTimeOffRequestComment(
    timeOffId: number,
    commentId: number
  ): Promise<IMentionAttachmentCommentData> {
    return this.axios
      .get<IMentionAttachmentCommentData>(
        `${this.endpoint}${timeOffId}/comments/${commentId}/`
      )
      .then(({ data }) => data);
  }

  createTimeOffRequestComment(
    timeOffId: number,
    comment: IMentionAttachmentCommentData
  ): Promise<IMentionAttachmentCommentData> {
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${timeOffId}/comments/`,
        comment
      )
      .then(({ data }) => data);
  }

  updateTimeOffRequestComment(
    timeOffId: number,
    comment: IMentionAttachmentCommentData
  ): Promise<IMentionAttachmentCommentData> {
    return this.axios
      .patch<IMentionAttachmentCommentData>(
        `${this.endpoint}${timeOffId}/comments/${comment.id}/`,
        comment
      )
      .then(({ data }) => data);
  }

  deleteTimeOffRequestComment(
    timeOffId: number,
    commentId: number
  ): Promise<void> {
    return this.axios
      .delete(`${this.endpoint}${timeOffId}/comments/${commentId}/`)
      .then(({ data }) => data);
  }

  addTimeOffRequestCommentAttachments(
    timeOffId: number,
    commentId: number,
    attachments: IAttachmentData[]
  ): Promise<IMentionAttachmentCommentData> {
    const formData = objectToFormData({ attachments });
    return this.axios
      .post<IMentionAttachmentCommentData>(
        `${this.endpoint}${timeOffId}/comments/${commentId}/add-attachments/`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      )
      .then(({ data }) => data);
  }

  removeTimeOffRequestCommentAttachment(
    timeOffId: number,
    commentId: number,
    attachmentId: number
  ): Promise<void> {
    return this.axios
      .delete(
        `${this.endpoint}${timeOffId}/comments/${commentId}/remove-attachment/${attachmentId}/`
      )
      .then();
  }

  fetchTimeOffRequestCommentsMentionableUsers(
    timeOffId: number
  ): Promise<IUserData[]> {
    return this.axios
      .get(`${this.endpoint}${timeOffId}/comments/mentionable-users/`)
      .then(({ data }) => data);
  }
}
